import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { loadingService } from '../../services/loadingService';

/**
 * Thin (3px) animated progress bar fixed at the top of the viewport.
 * Activated by:
 *   1. Any Axios API call (via loadingService increments from api.ts interceptors)
 *   2. Every React Router navigation (location change)
 */
const GlobalLoadingBar: React.FC = () => {
  const [state, setState] = useState<'idle' | 'running' | 'completing'>('idle');
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const location = useLocation();

  const start = () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setState('running');
    setProgress(0);
    let p = 0;
    intervalRef.current = window.setInterval(() => {
      p = p < 30 ? p + 15 : p < 60 ? p + 5 : p < 80 ? p + 1 : p;
      const capped = Math.min(p, 85);
      setProgress(capped);
      if (capped >= 85 && intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 80);
  };

  const complete = () => {
    if (intervalRef.current) { window.clearInterval(intervalRef.current); intervalRef.current = null; }
    setProgress(100);
    setState('completing');
    timeoutRef.current = window.setTimeout(() => setState('idle'), 400);
  };

  // API-driven loading
  useEffect(() => {
    return loadingService.subscribe(isLoading => {
      if (isLoading) start();
      else complete();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Route-change loading
  useEffect(() => {
    start();
    const t = window.setTimeout(complete, 200);
    return () => { window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  if (state === 'idle') return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: 3,
        width: `${progress}%`,
        background: 'linear-gradient(90deg, #56B0FF 0%, #7c3aed 100%)',
        boxShadow: '0 0 8px rgba(86,176,255,0.5)',
        borderRadius: '0 2px 2px 0',
        zIndex: 10000,
        pointerEvents: 'none',
        transition:
          state === 'running' ? 'width 0.25s ease' : 'width 0.15s ease, opacity 0.35s ease',
        opacity: state === 'completing' ? 0 : 1,
      }}
    />
  );
};

export default GlobalLoadingBar;
